import { motion } from 'framer-motion'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

const EASE = [0.22, 1, 0.36, 1] as const

const facts = [
  { value: '50M+', label: 'identities secured' },
  { value: '7,000+', label: 'enterprise integrations' },
  { value: '40+', label: 'countries' },
]

const shortBoilerplate =
  'Monofor builds enterprise identity software. IAM, PAM, and IGA on one platform, deployable self-hosted or in the cloud, used by banks, airports, retailers, and telcos to secure tens of millions of identities.'

const longBoilerplate =
  'Monofor is an identity and access platform that brings IAM, PAM, and IGA into a single product. Its three modules, Monosign (workforce identity), Monopam (privileged access), and Monosync (identity governance), share an API surface and can be deployed self-hosted or in the cloud. Monofor is used by financial institutions, public-sector agencies, manufacturers, and global retailers to secure tens of millions of identities across more than forty countries, with compliance posture aligned to SOC 2, ISO 27001, GDPR, KVKK, and PCI-DSS.'

export default function AboutBoilerplate() {
  return (
    <Section tone="subtle" density="default" id="about">
      <Container size="xl">
        <div className="max-w-3xl">
          <Eyebrow>About Monofor</Eyebrow>
          <Heading as="h2" size="display-lg" className="mt-5">
            Boilerplate
            <br />
            ready to paste.
          </Heading>
          <Text size="xl" tone="secondary" className="mt-6">
            Use the short version for news briefs and the long version for
            features and analyst write-ups. Both can be quoted without
            modification.
          </Text>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {facts.map((f, i) => (
            <motion.div
              key={f.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, ease: EASE, delay: i * 0.05 }}
              className="rounded-3xl border border-line-subtle bg-surface p-8"
            >
              <div className="text-display-lg font-medium text-ink-primary tabular-nums">
                {f.value}
              </div>
              <div className="mt-2 text-body-md text-ink-tertiary">
                {f.label}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <BoilerplateCard label="Short (50 words)" body={shortBoilerplate} />
          <BoilerplateCard label="Long (100 words)" body={longBoilerplate} />
        </div>
      </Container>
    </Section>
  )
}

function BoilerplateCard({ label, body }: { label: string; body: string }) {
  const copy = () => {
    navigator.clipboard?.writeText(body)
  }
  return (
    <div className="rounded-3xl border border-line-subtle bg-surface p-8 shadow-card">
      <div className="flex items-center justify-between">
        <Eyebrow tone="muted">{label}</Eyebrow>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 rounded-pill border border-line-subtle bg-surface-subtle px-3 py-1 text-caption font-medium text-ink-secondary transition hover:border-line hover:text-ink-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
        >
          Copy
        </button>
      </div>
      <Text size="md" tone="secondary" className="mt-5">
        {body}
      </Text>
    </div>
  )
}
